import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Edit2, Link2 } from 'lucide-react';
import SinceBeingTrackedSection from './SinceBeingTrackedSection';
import SimulationScenariosSection from './SimulationScenariosSection';
import GreeksMarketSection from './GreeksMarketSection';
import EditTargetModal from './EditTargetModal';
import OptionsChartWidget from './DashboardBuilder/OptionsChartWidget';
import AdvancedPriceChartWidget from './DashboardBuilder/AdvancedPriceChartWidget';
import CategoryAccordion from './DashboardBuilder/CategoryAccordion';
import IndicatorSettingsModal from './DashboardBuilder/IndicatorSettingsModal';
import { PnLSimulatorContent } from './PnLSimulatorModal';
import apiClient from '../services/api';
import { isUsageLimitError, getUsageLimitData } from '../utils/usageLimitHelper';
import UsageLimitModal from './UsageLimitModal';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import {
  calculateDaysUntilExpiration,
  calculateTargetProgress,
  formatRelativeTime,
  formatCurrency,
  formatPercentWithSign,
  getValueColorClass,
} from '../utils/contractCalculations';
import { INDICATOR_DEFAULTS } from '../utils/indicatorDefaults';
import { formatIndicatorDisplay, mergeCustomIndicators } from '../utils/indicatorCategories';
import { useCustomIndicators } from '../contexts/CustomIndicatorContext';
import { DEFAULT_BASELINE_MODE } from '../utils/watchlistBaseline';
import BaselineToggle from './Watchlist/BaselineToggle';

// localStorage keys for persisting indicator selections globally across all contracts
const EXPANDED_CARD_INDICATORS_KEY = 'expandedCard_indicators';
const EXPANDED_CARD_SHOW_BOLLINGER_KEY = 'expandedCard_showBollinger';
const EXPANDED_CARD_INDICATOR_SETTINGS_KEY = 'expandedCard_indicatorSettings';

export default function WatchlistContractCard({
  contract,
  isSelected,
  onSelect,
  onReset,
  onRefresh,
  onUpdateGroups,
  onDelete,
  onContractUpdate, // Callback when contract is updated
  baselineMode = DEFAULT_BASELINE_MODE,
  setBaselineMode,
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // 0=compact, 1=expanded with tabs
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [usageLimitData, setUsageLimitData] = useState(null);

  // Tab state for expanded view (resets to 'overview' when card is expanded)
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'charts'

  // Independent date ranges for each chart
  const [optionsDateRange, setOptionsDateRange] = useState('3m'); // Options data is limited, default 3M
  const [underlyingDateRange, setUnderlyingDateRange] = useState('1y'); // Stock data has more history

  // Crosshair synchronization state
  const [crosshairDate, setCrosshairDate] = useState(null);
  const [chartsLinked, setChartsLinked] = useState(false); // Only true when charts are "lined up"
  const crosshairDebounceRef = useRef(null);

  // Cross-chart data for enriched tooltips (matching OptionsPremiumChartModal)
  const [premiumDataAtCrosshair, setPremiumDataAtCrosshair] = useState(null);
  const [underlyingDataAtCrosshair, setUnderlyingDataAtCrosshair] = useState(null);

  // Indicator state for underlying chart (persisted globally via localStorage)
  const [indicators, setIndicators] = useState(() => {
    try {
      const saved = localStorage.getItem(EXPANDED_CARD_INDICATORS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [showBollinger, setShowBollinger] = useState(() => {
    try {
      const saved = localStorage.getItem(EXPANDED_CARD_SHOW_BOLLINGER_KEY);
      return saved === 'true';
    } catch {
      return false;
    }
  });

  const [indicatorSettings, setIndicatorSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(EXPANDED_CARD_INDICATOR_SETTINGS_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [showIndicatorDropdown, setShowIndicatorDropdown] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [hoveredIndicator, setHoveredIndicator] = useState(null);

  const dropdownRef = useRef(null);

  // Get custom indicators from context
  const { customIndicators } = useCustomIndicators();
  const mergedCategories = mergeCustomIndicators(customIndicators);

  // Shared API state for simulation data (with smart caching)
  const [simulationData, setSimulationData] = useState(null);
  const [simulationDataLoading, setSimulationDataLoading] = useState(false);
  const [simulationDataError, setSimulationDataError] = useState(null);

  // Extract contract data
  const {
    id,
    ticker,
    option_type,
    position_type = 'buy',
    expiration,
    strike,
    number_of_contracts = 1,
    average_cost_per_contract = 0,
    last_refresh_date,
    underlying_price_at_add = 0,
    current_underlying_price = 0,
    dynamic_days_to_gain,
    initial_days_to_gain,
    current_premium = 0,
    current_equity = 0,
    underlying_percent_change = 0,
    premium_percent_change = 0,
    equity_percent_change = 0,
    target_percent_change,
  } = contract;

  // --- Calculations ---
  const daysUntilExpiration = calculateDaysUntilExpiration(expiration);
  const targetProgress = calculateTargetProgress(
    underlying_price_at_add,
    current_underlying_price,
    target_percent_change
  );

  // --- Formatters ---
  const formatNumber = (n) => (n != null ? Number(n).toFixed(2) : '--');

  // --- Smart Caching: Load cached data first, then auto-refresh in background ---
  useEffect(() => {
    if (isExpanded) {
      // Step 1: Check if contract has cached_simulation_data
      if (contract.cached_simulation_data && contract.cached_simulation_data.length > 0) {
        console.log('📦 Loading cached simulation data for', ticker);
        setSimulationData(contract.cached_simulation_data);
        setSimulationDataLoading(false);
      }

      // Step 2: Auto-refresh in background (don't block UI)
      fetchSimulationDataInBackground();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  // Reset tab to overview when card is expanded
  useEffect(() => {
    if (isExpanded) {
      setActiveTab('overview');
    }
  }, [isExpanded]);

  // Close indicator dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowIndicatorDropdown(false);
      }
    };

    if (showIndicatorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showIndicatorDropdown]);

  // Persist indicators to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_CARD_INDICATORS_KEY, JSON.stringify(indicators));
    } catch {
      // Silently fail if localStorage unavailable
    }
  }, [indicators]);

  // Persist showBollinger to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_CARD_SHOW_BOLLINGER_KEY, String(showBollinger));
    } catch {
      // Silently fail if localStorage unavailable
    }
  }, [showBollinger]);

  // Persist indicatorSettings to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_CARD_INDICATOR_SETTINGS_KEY, JSON.stringify(indicatorSettings));
    } catch {
      // Silently fail if localStorage unavailable
    }
  }, [indicatorSettings]);

  // --- Background data refresh (silent, non-blocking) ---
  // Accepts optional contractOverride to use updated data (e.g., after target update)
  const fetchSimulationDataInBackground = async (contractOverride = null) => {
    try {
      const c = contractOverride || contract;
      const contractData = {
        ticker: c.ticker,
        option_type: c.option_type,
        position_type: c.position_type || 'buy',
        strike: c.strike,
        expiration: c.expiration,
        days_to_gain: c.dynamic_days_to_gain,
        number_of_contracts: c.number_of_contracts,
        average_cost_per_contract: c.average_cost_per_contract,
      };

      const response = await apiClient.post('/run-watchlist/', { contracts: [contractData] });

      if (response.data && response.data.length > 0) {
        console.log('✅ Background refresh complete for', ticker);
        setSimulationData(response.data);

        // Note: The backend refresh endpoint will save this data to cached_simulation_data
        // when the user triggers a manual refresh via the group-level "Refresh Data" button
      } else {
        setSimulationDataError('No simulation data returned');
      }
    } catch (err) {
      console.error('❌ Background refresh failed for', ticker, err);
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else {
        setSimulationDataError('Failed to load simulation data');
      }
    }
  };

  // --- Handlers ---
  const handleSelectChange = (e) => {
    e.stopPropagation();
    const checked = e.target.checked;
    onSelect(id, checked);
  };

  const handleCardClick = () => {
    setIsExpanded(!isExpanded); // Toggle between compact and expanded
  };

  const handleEditTarget = (e) => {
    e.stopPropagation();
    setEditModalOpen(true);
  };

  const handleTargetSaved = (updatedContract) => {
    if (onContractUpdate) {
      onContractUpdate(updatedContract);
    }
    // Auto-refresh simulation data to reflect new target settings
    // Pass updatedContract to use the new days_to_gain value immediately
    if (isExpanded) {
      console.log('🔄 Auto-refreshing simulation after target update for', ticker);
      fetchSimulationDataInBackground(updatedContract);
    }
  };

  const handleDelete = () => {
    onDelete?.(id);
    setConfirmingDelete(false);
  };

  // --- Tab style helper ---
  const getTabStyle = (tab) => ({
    padding: '6px 12px',
    borderRadius: '6px',
    border: activeTab === tab ? '2px solid #218aff' : '1px solid #333',
    background: activeTab === tab ? '#218aff20' : '#0a0a0a',
    color: activeTab === tab ? '#ffffff' : '#b0b0b0',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '12px',
    transition: 'all 0.2s ease'
  });

  // --- Indicator handlers ---
  const handleIndicatorToggle = (indicatorValue) => {
    // Check if this is a custom indicator
    const isCustom = indicatorValue.startsWith('custom_');
    let baseIndicator = indicatorValue;
    let customSettings = null;

    if (isCustom) {
      // Extract custom indicator ID and find its settings
      const customId = parseInt(indicatorValue.replace('custom_', ''));
      const customIndicator = customIndicators.find(ci => ci.id === customId);

      if (customIndicator) {
        baseIndicator = customIndicator.base_indicator;
        customSettings = customIndicator.settings;
      }
    }

    setIndicators(prev => {
      if (prev.includes(baseIndicator)) {
        return prev.filter(i => i !== baseIndicator);
      } else {
        return [...prev, baseIndicator];
      }
    });

    // If adding a custom indicator, apply its custom settings
    if (isCustom && customSettings && !indicators.includes(baseIndicator)) {
      setIndicatorSettings(prev => ({
        ...prev,
        [baseIndicator]: customSettings
      }));
    }

    // Auto-enable showBollinger when bollinger is added
    if (baseIndicator === 'bollinger' && !indicators.includes('bollinger')) {
      setShowBollinger(true);
    }
  };

  const handleOpenSettings = (indicator) => {
    setSelectedIndicator(indicator);
    setShowSettingsModal(true);
    setShowIndicatorDropdown(false);
  };

  const handleSaveSettings = async (indicator, settings) => {
    const newSettings = { ...indicatorSettings, [indicator]: settings };
    setIndicatorSettings(newSettings);
  };

  // --- "Line em up" handler: sync underlying chart to options chart date range AND enable crosshair ---
  const handleLineEmUp = () => {
    setUnderlyingDateRange(optionsDateRange);
    setChartsLinked(true); // Enable crosshair sync when charts are aligned
  };

  // --- Unlink charts handler: called when date ranges are manually changed ---
  const handleUnlinkCharts = () => {
    setChartsLinked(false);
    setCrosshairDate(null);
  };

  // --- Debounced crosshair synchronization handler (50ms debounce for performance) ---
  const handleCrosshairMove = useCallback((date) => {
    if (crosshairDebounceRef.current) {
      clearTimeout(crosshairDebounceRef.current);
    }
    crosshairDebounceRef.current = setTimeout(() => {
      setCrosshairDate(date);
      if (!date) {
        setPremiumDataAtCrosshair(null);
        setUnderlyingDataAtCrosshair(null);
      }
    }, 50);
  }, []);

  const handlePremiumCrosshairData = useCallback((dataPoint) => {
    setPremiumDataAtCrosshair(dataPoint);
  }, []);

  const handleUnderlyingCrosshairData = useCallback((dataPoint) => {
    setUnderlyingDataAtCrosshair(dataPoint);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (crosshairDebounceRef.current) {
        clearTimeout(crosshairDebounceRef.current);
      }
    };
  }, []);

  // --- Render Progress Bar ---
  const renderProgressBar = () => {
    if (!targetProgress) return null;

    const { actualChange, targetChange, progress } = targetProgress;
    const progressPercent = Math.min(Math.max(progress, 0), 100);
    const isNegative = progress < 0;
    const isExceeded = progress > 100;

    return (
      <div className="space-y-1">
        <div className="relative w-full bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${
              isNegative ? 'bg-red-500' : 'bg-green-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
          {isExceeded && (
            <div className="absolute -right-1 -top-1 text-yellow-400 text-xs">⚡</div>
          )}
        </div>
        <div className={`text-[11px] ${getValueColorClass(actualChange)}`}>
          {formatPercentWithSign(actualChange, 1)} of {formatPercentWithSign(targetChange, 0)}
        </div>
      </div>
    );
  };

  // --- Render Target Row ---
  const renderTargetRow = () => {
    // Check if at least one target field is set
    if (target_percent_change != null || initial_days_to_gain != null) {
      // Build target text based on what's available
      let targetText = "Exit: ";

      if (initial_days_to_gain != null) {
        targetText += `${dynamic_days_to_gain || '--'} days`;
      }

      if (target_percent_change != null) {
        if (initial_days_to_gain != null) {
          targetText += " • ";
        }
        targetText += `${formatPercentWithSign(target_percent_change, 0)} goal`;
      }

      return (
        <div
          className="text-xs text-gray-300 cursor-pointer hover:text-blue-400 transition mt-2 pt-2 border-t border-gray-700"
          onClick={handleEditTarget}
        >
          {targetText}
        </div>
      );
    } else {
      return (
        <div
          className="text-xs text-gray-400 cursor-pointer hover:text-blue-400 transition mt-2 pt-2 border-t border-gray-700"
          onClick={handleEditTarget}
        >
          No Exit Set - Click to Set
        </div>
      );
    }
  };

  // ===================
  // LEVEL 0: COMPACT CARD
  // ===================
  const renderCompactCard = () => (
    <div
      className="cursor-pointer transition-all duration-200"
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelectChange}
            className="flex-shrink-0 mt-1"
          />
          <div className="text-brand-white font-medium text-sm leading-tight">
            {ticker} {position_type?.toUpperCase()} {option_type?.toUpperCase()} @ ${formatNumber(strike)}
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
          className="text-blue-400 text-xs hover:text-blue-300 transition flex-shrink-0"
        >
          ▾
        </button>
      </div>

      {/* Expiration Info */}
      <div className="text-xs text-gray-400 mb-3">
        📅 {expiration} • ⏱ {daysUntilExpiration} days left
      </div>

      {/* Three-Column Metrics */}
      <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
        {/* Premium */}
        <div>
          <div className="text-gray-500 text-[10px] mb-1 cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content="Current market premium per share. % change is from your avg cost per contract."
          >Premium</div>
          <div className="font-medium">{formatCurrency(current_premium)}</div>
          <div className={getValueColorClass(premium_percent_change)}>
            {formatPercentWithSign(premium_percent_change, 1)}
          </div>
        </div>

        {/* Equity */}
        <div>
          <div className="text-gray-500 text-[10px] mb-1 cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content="Total equity = Contracts x Premium x 100. % change is from initial equity when saved."
          >
            Equity
          </div>
          <div className="font-medium">{formatCurrency(current_equity)}</div>
          <div className={getValueColorClass(equity_percent_change)}>
            {formatPercentWithSign(equity_percent_change, 1)}
          </div>
        </div>

        {/* Underlying */}
        <div>
          <div className="text-gray-500 text-[10px] mb-1 cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content="Current stock price. % change is from the price when you started tracking."
          >Underlying</div>
          <div className="font-medium">{formatCurrency(current_underlying_price)}</div>
          <div className={getValueColorClass(underlying_percent_change)}>
            {formatPercentWithSign(underlying_percent_change, 1)}
          </div>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        {/* Target Days Progress */}
        <div>
          <div className="text-gray-500 text-[10px] mb-1 cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content="Days elapsed toward your exit holding period. Counts down daily from when saved or last reset."
          >Exit</div>
          {initial_days_to_gain != null ? (
            <div className="text-gray-300 text-[11px]">
              {initial_days_to_gain - (dynamic_days_to_gain || 0)} of {initial_days_to_gain} days
            </div>
          ) : target_percent_change != null ? (
            <div className="text-gray-300 text-[11px]">
              {formatPercentWithSign(target_percent_change, 0)} goal
            </div>
          ) : (
            <div className="text-gray-500 text-[10px]">Not set</div>
          )}
        </div>

        {/* Target % Progress */}
        <div>
          <div className="text-gray-500 text-[10px] mb-1 cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content="How far the underlying has moved toward your exit % change goal."
          >Exit % Progress</div>
          {targetProgress ? (
            renderProgressBar()
          ) : (
            <div className="text-gray-500 text-[10px]">No exit set</div>
          )}
        </div>

        {/* Updated */}
        <div>
          <div className="text-gray-500 text-[10px] mb-1 cursor-help"
            data-tooltip-id="watchlist-card-tooltip"
            data-tooltip-content="Time since last data refresh from market."
          >
            Updated
          </div>
          <div className="text-gray-400 text-[10px]">
            {formatRelativeTime(last_refresh_date)}
          </div>
        </div>
      </div>

      {/* Target Row */}
      {renderTargetRow()}
    </div>
  );

  // ===================
  // LEVEL 1: EXPANDED (VERTICAL SECTIONS WITH TABS)
  // ===================
  const renderExpandedCard = () => (
    <div className="space-y-3">
      {/* Header - clickable to collapse */}
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-gray-800/30 rounded px-2 py-1 transition"
        onClick={handleCardClick}
      >
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelectChange}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0"
          />
          <div className="text-brand-white font-medium text-sm">
            {ticker} {position_type?.toUpperCase()} {option_type?.toUpperCase()} @ ${formatNumber(strike)}
            <span className="text-gray-400 text-xs ml-2">• {expiration} • {daysUntilExpiration} days left</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {/* Tab Buttons */}
          <button
            onClick={() => setActiveTab('overview')}
            style={getTabStyle('overview')}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('charts')}
            style={getTabStyle('charts')}
          >
            Historical Charts
          </button>
          <button
            onClick={() => setActiveTab('simulated')}
            style={getTabStyle('simulated')}
          >
            Simulated Returns
          </button>

          {/* Baseline (cost basis) toggle — synced with the table-level toggle */}
          {setBaselineMode && (
            <div className="ml-2"
              data-tooltip-id="watchlist-card-tooltip"
              data-tooltip-content="Baseline for simulated returns. 'Avg Cost' compares to your purchase price; 'Current' compares to today's market premium."
            >
              <BaselineToggle mode={baselineMode} onChange={setBaselineMode} size="sm" />
            </div>
          )}

          {/* Settings and Collapse */}
          <button
            onClick={handleEditTarget}
            className="text-gray-400 hover:text-blue-400 transition text-sm ml-2"
            title="Edit Exit"
          >
            ⚙
          </button>
          <span className="text-blue-400 text-xs cursor-pointer" onClick={handleCardClick}>▲</span>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* First row: Since Being Tracked | Greeks & Market Data */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SinceBeingTrackedSection contract={contract} simulationData={simulationData?.[0]} />
            <GreeksMarketSection
              contract={contract}
              simulationData={simulationData?.[0]}
              loading={simulationDataLoading}
              error={simulationDataError}
            />
          </div>

          {/* Second row: Simulation Scenarios (full width) */}
          <SimulationScenariosSection
            contract={contract}
            simulationData={simulationData}
            loading={simulationDataLoading}
            error={simulationDataError}
          />
        </div>
      )}

      {activeTab === 'charts' && (
        <div className="space-y-2">
          {/* Chart Controls Row - independent date ranges with "Line em up" button */}
          <div className="flex items-center justify-between px-2" onClick={(e) => e.stopPropagation()}>
            {/* Left: Options Premium date range */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Premium:</span>
              <select
                value={optionsDateRange}
                onChange={(e) => {
                  setOptionsDateRange(e.target.value);
                  handleUnlinkCharts(); // Unlink when date range is manually changed
                }}
                className="bg-background text-text text-xs px-2 py-1 rounded border border-muted hover:border-primary transition"
              >
                <option value="1m">1M</option>
                <option value="3m">3M</option>
                <option value="6m">6M</option>
                <option value="1y">1Y</option>
              </select>
            </div>

            {/* Center: "Line em up" button - syncs date ranges and enables crosshair */}
            <button
              onClick={chartsLinked ? handleUnlinkCharts : handleLineEmUp}
              className={`text-xs px-3 py-1 rounded border transition flex items-center gap-1.5 ${
                chartsLinked
                  ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                  : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-blue-500 hover:text-white'
              }`}
              title={chartsLinked ? "Charts are linked - crosshair synced" : "Sync charts and enable crosshair"}
            >
              <Link2 className="w-3.5 h-3.5" />
              <span>{chartsLinked ? 'Linked' : 'Line em up'}</span>
            </button>

            {/* Right: Indicators + Underlying date range */}
            <div className="flex items-center gap-2">
              {/* Indicators Dropdown (affects underlying chart only) */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowIndicatorDropdown(!showIndicatorDropdown)}
                  className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition flex items-center gap-1"
                  title="Indicators (Underlying)"
                >
                  <span>Indicators</span>
                  {indicators.length > 0 && (
                    <span className="px-1.5 py-0.5 bg-blue-600/30 text-blue-400 rounded text-[10px]">
                      {indicators.length}
                    </span>
                  )}
                  <ChevronDown className="w-3 h-3" />
                </button>

                {showIndicatorDropdown && (
                  <div
                    className="absolute top-full right-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-50 min-w-64 max-h-80 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Active Indicators */}
                    <div className="px-3 py-2 border-b border-gray-700">
                      <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Active Indicators</p>
                      <div className="space-y-1">
                        {indicators.length > 0 ? (
                          indicators.map(ind => (
                            <div
                              key={ind}
                              className="flex items-center justify-between text-sm text-gray-200 px-2 py-1 rounded hover:bg-gray-700/50 group"
                              onMouseEnter={() => setHoveredIndicator(ind)}
                              onMouseLeave={() => setHoveredIndicator(null)}
                            >
                              <button
                                onClick={() => handleOpenSettings(ind)}
                                className="flex-1 text-left hover:text-blue-400 transition-colors"
                                title="Click to edit settings"
                              >
                                {formatIndicatorDisplay(ind, indicatorSettings[ind])}
                              </button>
                              <div className="flex items-center gap-1">
                                {hoveredIndicator === ind && (
                                  <button
                                    onClick={() => handleOpenSettings(ind)}
                                    className="text-gray-400 hover:text-white transition-colors px-1"
                                    title="Edit settings"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleIndicatorToggle(ind)}
                                  className="text-gray-400 hover:text-red-400 text-xs"
                                  title="Remove indicator"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">None</span>
                        )}
                      </div>
                    </div>

                    {/* Bollinger Toggle (if active) */}
                    {indicators.includes('bollinger') && (
                      <div className="px-3 py-2 border-b border-gray-700">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showBollinger}
                            onChange={(e) => setShowBollinger(e.target.checked)}
                            className="w-3 h-3"
                          />
                          <span className="text-xs text-gray-300">Show Bollinger Bands</span>
                        </label>
                      </div>
                    )}

                    {/* Available Indicators */}
                    <div className="px-3 py-2">
                      <p className="text-xs text-gray-400 mb-2">Add Indicators:</p>
                      <CategoryAccordion
                        categories={mergedCategories}
                        selectedItems={indicators}
                        onToggle={handleIndicatorToggle}
                        onEdit={handleOpenSettings}
                        displayMode="compact"
                      />
                    </div>
                  </div>
                )}
              </div>

              <span className="text-xs text-gray-400">Price:</span>
              <select
                value={underlyingDateRange}
                onChange={(e) => {
                  setUnderlyingDateRange(e.target.value);
                  handleUnlinkCharts(); // Unlink when date range is manually changed
                }}
                className="bg-background text-text text-xs px-2 py-1 rounded border border-muted hover:border-primary transition"
              >
                <option value="1m">1M</option>
                <option value="3m">3M</option>
                <option value="6m">6M</option>
                <option value="1y">1Y</option>
                <option value="2y">2Y</option>
                <option value="5y">5Y</option>
              </select>
            </div>
          </div>

          {/* Side-by-side charts with crosshair sync (only when linked) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Options Premium Chart */}
            <div className="bg-gray-950 rounded overflow-hidden">
              <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-800 font-medium">
                Options Premium
              </div>
              <div className="h-[450px]">
                <OptionsChartWidget
                  widget={{
                    id: `contract-${id}-premium`,
                    ticker,
                    config: {
                      option_type,
                      strike,
                      expiration,
                      date_range: optionsDateRange,
                    },
                  }}
                  isLinked={chartsLinked}
                  onCrosshairMove={handleCrosshairMove}
                  sharedCrosshair={crosshairDate}
                  onCrosshairData={handlePremiumCrosshairData}
                  crossChartData={underlyingDataAtCrosshair}
                  livePrice={current_premium || null}
                  liveTimestamp={last_refresh_date || null}
                />
              </div>
            </div>

            {/* Right: Underlying Price Chart */}
            <div className="bg-gray-950 rounded overflow-hidden">
              <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-800 font-medium">
                Underlying Price
              </div>
              <div className="h-[450px]">
                <AdvancedPriceChartWidget
                  ticker={ticker}
                  dateRange={underlyingDateRange}
                  indicators={indicators}
                  indicatorConfig={
                    // Only use config path when user has actually customized indicator settings
                    indicators.length > 0 && Object.keys(indicatorSettings).some(key => indicators.includes(key))
                      ? indicators.reduce((config, ind) => {
                          const defaultSettings = INDICATOR_DEFAULTS[ind] || {};
                          const customSettings = indicatorSettings[ind] || {};
                          config[ind] = {
                            ...defaultSettings,
                            ...customSettings,
                            colors: {
                              ...defaultSettings.colors,
                              ...customSettings.colors
                            }
                          };
                          return config;
                        }, {})
                      : null
                  }
                  isLinked={chartsLinked}
                  onCrosshairMove={handleCrosshairMove}
                  crosshairDate={crosshairDate}
                  showBollinger={showBollinger}
                  setShowBollinger={setShowBollinger}
                  onCrosshairData={handleUnderlyingCrosshairData}
                  crossChartData={premiumDataAtCrosshair}
                  livePrice={current_underlying_price || null}
                  liveTimestamp={last_refresh_date || null}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'simulated' && (
        <div className="space-y-2">
          {simulationDataLoading && !simulationData ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3"></div>
              <span className="text-sm text-gray-400">Loading simulation data...</span>
            </div>
          ) : simulationDataError && !simulationData ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-red-400 text-sm">{simulationDataError}</p>
            </div>
          ) : simulationData?.[0] ? (
            <PnLSimulatorContent rowData={simulationData[0]} baselineMode={baselineMode} />
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
              Simulation data not available. Try expanding the card again.
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        className={`bg-gray-900 border rounded-lg p-3 transition-all duration-200 ${
          isSelected
            ? 'border-blue-500 ring-2 ring-blue-500/50'
            : 'border-gray-700 hover:border-gray-600'
        } ${isExpanded ? 'col-span-full' : ''}`}
      >
        {/* Render appropriate level */}
        {!isExpanded && renderCompactCard()}
        {isExpanded && renderExpandedCard()}
      </div>

      {/* Edit Target Modal */}
      <EditTargetModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        contract={contract}
        onSave={handleTargetSaved}
      />

      {/* Confirm Delete Modal */}
      {confirmingDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-80 text-center space-y-4 border border-gray-600">
            <p className="font-semibold text-white">
              Are you sure you want to remove this contract?
            </p>
            <div className="flex justify-center gap-4">
              <button onClick={handleDelete} className="btn-danger">
                Yes, Remove
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="btn-dark"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Indicator Settings Modal */}
      {showSettingsModal && selectedIndicator && (
        <IndicatorSettingsModal
          isOpen={showSettingsModal}
          onClose={() => {
            setShowSettingsModal(false);
            setSelectedIndicator(null);
          }}
          onSave={handleSaveSettings}
          indicator={selectedIndicator}
          currentSettings={indicatorSettings[selectedIndicator] || {}}
        />
      )}

      {usageLimitData && (
        <UsageLimitModal data={usageLimitData} onClose={() => setUsageLimitData(null)} />
      )}

      {/* Shared tooltip portal for contract cards and child components */}
      <ReactTooltip
        id="watchlist-card-tooltip"
        place="top"
        delayShow={300}
        delayHide={100}
        style={{
          backgroundColor: '#111827',
          color: '#ffffff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '13px',
          maxWidth: '280px',
          lineHeight: '1.4',
          zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          border: '1px solid #374151',
        }}
      />
    </>
  );
}

